import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  FileAudio,
  AlertTriangle,
  ShieldOff,
  Trash2,
  MessageSquare,
  Bell,
  X,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  POPUP_MAX_VISIBLE,
  type NotificationPopup,
} from "@shared/notificationPopups";

/**
 * Всплывающие уведомления поверх экрана.
 *
 * Зачем, если есть колокольчик. Владелица 21.09.2026: «не колокольчик, а чтобы
 * хоп — справа всплыло, что расшифровка готова, и я прямо оттуда перешла на
 * запись. И чтобы висело, пока не закрыл или не перешёл. И звук».
 * Колокольчик показывает уведомление только тому, кто сам решил туда
 * заглянуть, — то есть тому, кто и так помнит, что чего-то ждёт.
 *
 * Три правила, каждое — из её слов:
 *  • само не исчезает. Ни таймера, ни автозакрытия: расшифровка приходит через
 *    15 минут, человек в это время говорит по телефону и всплывашку, которая
 *    погасла через пять секунд, просто не увидит;
 *  • ведёт сразу в дело, а не «в карточку, а дальше сам»;
 *  • звучит. Негромко, один раз на уведомление.
 */

const ICONS: Record<string, { icon: typeof Bell; bg: string }> = {
  video_transcript_ready: { icon: FileAudio, bg: "bg-emerald-600" },
  video_transcript_failed: { icon: AlertTriangle, bg: "bg-orange-500" },
  video_consent_withdrawn: { icon: ShieldOff, bg: "bg-red-600" },
  video_deletion_requested: { icon: Trash2, bg: "bg-amber-600" },
  video_deletion_decided: { icon: Trash2, bg: "bg-slate-600" },
  chat_mention: { icon: MessageSquare, bg: "bg-slate-800" },
};

/** Как часто спрашиваем сервер. 15 секунд — «хоп» на глаз, нагрузки никакой. */
const POLL_MS = 15_000;

/**
 * Что уже показывали в этом браузере. Без отметки каждое обновление страницы
 * вываливало бы одни и те же карточки заново, а пометить их прочитанными
 * нельзя: человек ещё не ответил, и в колокольчике они должны остаться
 * непрочитанными.
 */
const SEEN_KEY = "notification-popups-seen";

function readSeen(): Set<number> {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const list = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(list) ? list.map(Number).filter(Boolean) : []);
  } catch {
    // Приватное окно, запрет на хранение, битое значение — всё это не повод
    // остаться без уведомлений: просто покажем их снова.
    return new Set();
  }
}

function writeSeen(ids: Set<number>): void {
  try {
    // Держим последние 200: список нужен только чтобы не повторяться, а расти
    // без края ему незачем.
    const list = [...ids].sort((a, b) => b - a).slice(0, 200);
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(list));
  } catch {
    // Хранилище недоступно — переживём, в худшем случае карточка появится
    // ещё раз после перезагрузки.
  }
}

/**
 * Короткий звук без файла: два мягких тона через Web Audio.
 *
 * Файлом было бы проще, но он тянет лишний запрос и кэш, а нужен один сигнал
 * на полсекунды. Браузер запрещает звук до первого действия человека на
 * странице — в этом случае тихо выходим: уведомление всё равно видно глазами.
 */
let audioCtx: AudioContext | null = null;
let wakeupBound = false;

/**
 * Один общий контекст на вкладку и «пробуждение» по первому же действию
 * человека.
 *
 * Браузер запрещает звук, пока со страницей не повзаимодействовали, и
 * созданный до этого контекст остаётся спящим навсегда, если его не
 * разбудить. Поэтому вешаем одноразовый слушатель на первый клик или нажатие
 * клавиши: к моменту, когда придёт первое уведомление, звук уже разрешён.
 */
function audioContext(): AudioContext | null {
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  if (!wakeupBound) {
    wakeupBound = true;
    const wake = () => void audioCtx?.resume().catch(() => {});
    window.addEventListener("pointerdown", wake, { once: true });
    window.addEventListener("keydown", wake, { once: true });
  }
  return audioCtx;
}

function playChime(): void {
  try {
    const ctx = audioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.13, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
    gain.connect(ctx.destination);
    for (const [freq, at] of [
      [880, 0],
      [1174.7, 0.12],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + at);
      osc.connect(gain);
      osc.start(now + at);
      osc.stop(now + at + 0.3);
    }
    // Контекст не закрываем: он общий и переиспользуется. Закрытый пришлось бы
    // создавать заново, а новый снова оказался бы спящим.
  } catch {
    // Звук — приятная мелочь, а не условие работы. Молчим.
  }
}

export function NotificationPopups() {
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const [seen, setSeen] = useState<Set<number>>(() => readSeen());
  /** Закрытые крестиком в этом сеансе — не всплывают до перезагрузки. */
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());
  const soundedRef = useRef<Set<number>>(new Set());

  const { data = [] } = trpc.notifications.popups.useQuery(undefined, {
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1_000,
    refetchOnWindowFocus: true,
  });

  const visible = useMemo(() => {
    const items = (data as NotificationPopup[]).filter(
      item => !seen.has(item.id) && !dismissed.has(item.id)
    );
    // Сверху — свежее: id растёт со временем.
    return items.sort((a, b) => b.id - a.id).slice(0, POPUP_MAX_VISIBLE);
  }, [data, seen, dismissed]);

  // Звук — один раз на уведомление, даже если карточка висит часами и список
  // приходит заново каждые пятнадцать секунд.
  useEffect(() => {
    const fresh = visible.filter(item => !soundedRef.current.has(item.id));
    if (!fresh.length) return;
    fresh.forEach(item => soundedRef.current.add(item.id));
    playChime();
  }, [visible]);

  const markAsRead = trpc.notifications.markAsRead.useMutation({
    onSuccess: () => {
      void utils.notifications.popups.invalidate();
      void utils.notifications.getUnreadCount.invalidate();
      void utils.notifications.getAll.invalidate();
    },
  });

  const remember = useCallback((id: number) => {
    setSeen(current => {
      const next = new Set(current);
      next.add(id);
      writeSeen(next);
      return next;
    });
  }, []);

  const open = useCallback(
    (item: NotificationPopup) => {
      // Переход — это и есть ответ на уведомление: помечаем прочитанным, чтобы
      // оно не ждало второй раз ещё и в колокольчике.
      markAsRead.mutate({ id: item.id });
      remember(item.id);
      if (item.link) navigate(item.link);
    },
    [markAsRead, navigate, remember]
  );

  const dismiss = useCallback(
    (id: number) => {
      // Крестик убирает карточку с глаз, но НЕ помечает прочитанным: человек
      // сказал «не сейчас», а не «разобрался». В колокольчике остаётся.
      setDismissed(current => new Set(current).add(id));
      remember(id);
    },
    [remember]
  );

  if (!visible.length) return null;

  return (
    <div
      /* Ниже шапки: иначе карточка закрывает колокольчик и профиль — те самые
         кнопки, которыми человек пользуется, пока читает уведомление. */
      className="pointer-events-none fixed right-3 z-[60] flex w-[calc(100vw-24px)] max-w-sm flex-col gap-2 sm:right-5 sm:w-[380px]"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 60px)" }}
      role="region"
      aria-label="Новые уведомления"
    >
      {visible.map(item => {
        const look = ICONS[item.type] ?? { icon: Bell, bg: "bg-slate-500" };
        const Icon = look.icon;
        return (
          <article
            key={item.id}
            className="pointer-events-auto flex gap-3 rounded-2xl border border-[#e5e5ea] bg-white p-3.5 shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
          >
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${look.bg}`}
            >
              <Icon className="h-[18px] w-[18px] text-white" />
            </div>
            <div className="min-w-0 flex-1">
              {/* Имена не обрезаем — переносим. */}
              <p className="text-[13px] font-semibold leading-5 text-[#1d1d1f]">
                {item.title}
              </p>
              <p className="mt-0.5 text-[12px] leading-[18px] text-[#636366]">
                {item.message}
              </p>
              {item.link && (
                <button
                  type="button"
                  onClick={() => open(item)}
                  className="mt-2 inline-flex min-h-8 items-center rounded-lg bg-[#000b3e] px-3 text-[12px] font-medium text-white hover:bg-[#16204f]"
                >
                  Открыть
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              aria-label="Закрыть уведомление"
              className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#aeaeb2] hover:bg-[#f5f5f7] hover:text-[#636366]"
            >
              <X className="h-4 w-4" />
            </button>
          </article>
        );
      })}
    </div>
  );
}
